import { useCallback, useEffect, useState } from 'react';
import { useAccess } from '@/access/useAccess';
import { billingApi } from '@/api/endpoints/billing';
import { isApiError } from '@/api/errors';
import type { PlanSlug, UserSubscription } from '@/api/types';
import { track } from '@/analytics/tracker';
import pageStyles from '@/app/shell/Page.module.css';
import { useAuth } from '@/auth/AuthProvider';
import {
  formatBillingDate,
  paidRowKind,
  paidSubscriptionOf,
  resolveBillingState,
  type BillingKind,
} from '@/billing/billingState';
import paywall from '@/billing/Paywall.module.css';
import { PlanCards } from '@/billing/PlanCards';
import { pollMe, useCheckout, useInvalidateCatalog } from '@/billing/useCheckout';
import { formatPrice, useSubscriptionPlans } from '@/billing/useSubscriptionPlans';
import { appConfig } from '@/config/app';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import { uuidv4 } from '@/lib/ids';
import styles from './Billing.module.css';

/** Отказ запроса → компактный код причины для телеметрии (без текста платёжки). */
function errorType(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 409) return 'conflict';
    if (error.status === 422) return 'validation';
    if (error.status === 429) return 'rate_limited';
    return `http_${error.status}`;
  }
  return 'network';
}

type Pending = 'portal' | 'change' | 'cancel' | 'resume' | null;
/** Откуда открыт Customer Portal — для телеметрии `portal_*`. */
type PortalSource = 'invoices' | 'manage_payment' | 'update_payment' | 'resume_paused';
/** Состояние платной строки Paddle — всё, кроме free. */
type PaidKind = Exclude<BillingKind, 'free'>;
const MUTATION_POLL = { intervalMs: 1500, timeoutMs: 30_000 };

function planSlugOf(row: UserSubscription | null): PlanSlug | null {
  const slug = row?.plan?.slug;
  return slug === 'monthly' || slug === 'yearly' ? slug : null;
}

/**
 * Страница биллинга (BILL-7): free · active · grace · past_due · paused; в бесплатном
 * режиме — без покупки. Карточка «Текущий план» — строка доступа (грант или оплата);
 * платная строка Paddle, которую грант прячет, — отдельной карточкой «Paid subscription».
 * Мутации (Switch / Cancel / Resume) и опрос /me после них смотрят на платную строку.
 */
export function BillingPage() {
  const { user, fetchUser } = useAuth();
  const access = useAccess();
  const prices = useSubscriptionPlans();
  const invalidateCatalog = useInvalidateCatalog();
  const billing = resolveBillingState(user);
  const t = useT();
  const [plan, setPlan] = useState<PlanSlug>('yearly');
  const [pending, setPending] = useState<Pending>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkout = useCheckout({ surface: 'billing' });

  // Вебхук мог прийти только что — на входе перечитываем пользователя.
  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  const expires = formatBillingDate(billing.expiresAt);
  // Смена тарифа и отмена — у платной строки, даже когда «Текущий план» показывает грант.
  const paidPlan = planSlugOf(billing.paid) ?? billing.plan;
  const otherPlan: PlanSlug = paidPlan === 'yearly' ? 'monthly' : 'yearly';

  const openPortal = useCallback(async (source: PortalSource) => {
    const correlation_id = uuidv4();
    track('portal_request', { correlation_id, metadata: { source } });
    setPending('portal');
    setError(null);
    try {
      const { url } = await billingApi.portal(correlation_id);
      if (!url) throw new Error(t('billing.portalUnavailable'));
      track('portal_success', { correlation_id, metadata: { source } });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      track('portal_failed', { correlation_id, metadata: { source, error_type: errorType(e) } });
      setError((e as Error)?.message || t('billing.portalFailed'));
    } finally {
      setPending(null);
    }
  }, []);

  const changePlan = useCallback(async () => {
    const correlation_id = uuidv4();
    const meta = { from_plan: paidPlan, to_plan: otherPlan };
    track('change_plan_request', { correlation_id, metadata: meta });
    setPending('change');
    setError(null);
    try {
      await billingApi.changePlan(otherPlan, correlation_id);
      const result = await pollMe(
        (u) => paidSubscriptionOf(u)?.plan?.slug === otherPlan,
        MUTATION_POLL,
      );
      await fetchUser();
      invalidateCatalog();
      track(result.ok ? 'change_plan_success' : 'change_plan_failed', {
        correlation_id,
        metadata: result.ok ? meta : { ...meta, error_type: 'activation_timeout' },
      });
    } catch (e) {
      track('change_plan_failed', {
        correlation_id,
        metadata: { ...meta, error_type: errorType(e) },
      });
      setError((e as Error)?.message || t('billing.changePlanFailed'));
    } finally {
      setPending(null);
    }
  }, [paidPlan, otherPlan, fetchUser, invalidateCatalog]);

  const cancel = useCallback(async () => {
    const correlation_id = uuidv4();
    track('cancel_request', { correlation_id, metadata: { plan: paidPlan } });
    setPending('cancel');
    setError(null);
    try {
      await billingApi.cancel(correlation_id);
      const result = await pollMe((u) => !!paidSubscriptionOf(u)?.cancelled_at, MUTATION_POLL);
      await fetchUser();
      setConfirmCancel(false);
      track(result.ok ? 'cancel_success' : 'cancel_failed', {
        correlation_id,
        metadata: result.ok
          ? { plan: paidPlan }
          : { plan: paidPlan, error_type: 'activation_timeout' },
      });
    } catch (e) {
      track('cancel_failed', {
        correlation_id,
        metadata: { plan: paidPlan, error_type: errorType(e) },
      });
      setError((e as Error)?.message || t('billing.cancelFailed'));
    } finally {
      setPending(null);
    }
  }, [paidPlan, fetchUser]);

  const resume = useCallback(async () => {
    const correlation_id = uuidv4();
    track('resume_request', { correlation_id, metadata: { source: 'billing_page' } });
    setPending('resume');
    setError(null);
    try {
      await billingApi.resume(correlation_id);
      const result = await pollMe((u) => {
        const paid = paidSubscriptionOf(u);
        return !!paid && !paid.cancelled_at;
      }, MUTATION_POLL);
      await fetchUser();
      invalidateCatalog();
      track(result.ok ? 'resume_success' : 'resume_failed', {
        correlation_id,
        metadata: result.ok
          ? { source: 'billing_page' }
          : { source: 'billing_page', error_type: 'activation_timeout' },
      });
    } catch (e) {
      track('resume_failed', {
        correlation_id,
        metadata: { source: 'billing_page', error_type: errorType(e) },
      });
      setError((e as Error)?.message || t('billing.resumeFailed'));
    } finally {
      setPending(null);
    }
  }, [fetchUser, invalidateCatalog]);

  const subscribe = (slug: PlanSlug) => {
    track('subscribe_click', { metadata: { source: 'billing_page', plan: slug } });
    void checkout.start(slug);
  };

  const planNameOf = (slug: PlanSlug | null) =>
    slug === 'yearly' ? t('billing.proYearly') : t('billing.proMonthly');
  const billedNote = (slug: PlanSlug | null) =>
    t(slug === 'yearly' ? 'billing.billedYearly' : 'billing.billedMonthly', {
      price: formatPrice(slug === 'yearly' ? prices.yearly : prices.monthly),
    });
  const badgeOf = (kind: BillingKind) =>
    kind === 'free'
      ? t('billing.badgeFree')
      : kind === 'grace'
        ? t('billing.badgeEnding')
        : kind === 'past_due'
          ? t('billing.badgePastDue')
          : kind === 'paused'
            ? t('billing.badgePaused')
            : t('billing.badgeActive');
  const badgeClass = (kind: BillingKind) =>
    cx(
      styles.badge,
      (kind === 'active' || kind === 'grace') && styles.badgeAccent,
      (kind === 'past_due' || kind === 'paused') && styles.badgeWarn,
    );

  const planName = billing.kind === 'free' ? t('billing.free') : planNameOf(billing.plan);
  const planNote =
    billing.kind === 'free'
      ? billing.paid
        ? t('billing.freePaidBelow')
        : access.isPaywallEnabled
          ? t('billing.freeNote')
          : t('billing.freeModeNote')
      : billing.kind === 'grace'
        ? t('billing.graceNote')
        : billing.kind === 'past_due'
          ? t('billing.pastDueNote')
          : billing.kind === 'paused'
            ? t('billing.pausedNote')
            : billedNote(billing.plan);
  const busy = pending !== null || checkout.isBusy;
  const shownError = error ?? checkout.error;
  const paymentMethod = user?.subscription?.payment_method ?? null;

  /** Ссылка на счета в Customer Portal — внутри сетки фактов. */
  const invoicesFact = (
    <>
      <dt>{t('billing.invoices')}</dt>
      <dd>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => void openPortal('invoices')}
          disabled={busy}
        >
          {t('billing.portal')}
        </button>
      </dd>
    </>
  );

  /**
   * Кнопки платной строки по её состоянию. Grace — Resume без новой оплаты; пауза и
   * past_due — только Customer Portal (возобновить паузу и обновить карту можно лишь там);
   * active — Switch / Manage payment / Cancel с инлайн-подтверждением.
   */
  const renderPaidActions = (kind: PaidKind) => {
    if (kind === 'grace') {
      return (
        <button
          type="button"
          className={cx(paywall.primary, styles.subscribe)}
          onClick={() => void resume()}
          disabled={busy}
        >
          {pending === 'resume' ? t('billing.resuming') : t('billing.resume')}
        </button>
      );
    }
    if (kind === 'paused') {
      return (
        <button
          type="button"
          className={cx(paywall.primary, styles.subscribe)}
          onClick={() => void openPortal('resume_paused')}
          disabled={busy}
        >
          {t('billing.resumeInPortal')}
        </button>
      );
    }
    return (
      <>
        {kind === 'past_due' ? (
          <button
            type="button"
            className={cx(paywall.primary, styles.subscribe)}
            onClick={() => void openPortal('update_payment')}
            disabled={busy}
          >
            {t('billing.updatePayment')}
          </button>
        ) : null}
        <div className={styles.row}>
          {kind === 'active' ? (
            <>
              <button
                type="button"
                className={styles.ghost}
                onClick={() => void changePlan()}
                disabled={busy || !access.isPaywallEnabled}
              >
                {pending === 'change'
                  ? t('billing.switching')
                  : paidPlan === 'yearly'
                    ? t('billing.switchToMonthly')
                    : t('billing.switchToYearly')}
              </button>
              <button
                type="button"
                className={styles.ghost}
                onClick={() => void openPortal('manage_payment')}
                disabled={busy}
              >
                {t('billing.managePayment')}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className={styles.quiet}
            onClick={() => setConfirmCancel(true)}
            disabled={busy || confirmCancel}
          >
            {t('billing.cancel')}
          </button>
        </div>
        {confirmCancel ? (
          <div className={styles.confirm} role="group" aria-label={t('billing.confirmCancel')}>
            <span className={styles.confirmText}>
              {t('billing.cancelConfirm', { date: expires })}
            </span>
            <button
              type="button"
              className={styles.confirmPrimary}
              onClick={() => void cancel()}
              disabled={busy}
            >
              {pending === 'cancel' ? t('billing.cancelling') : t('common.confirm')}
            </button>
            <button
              type="button"
              className={styles.confirmGhost}
              onClick={() => setConfirmCancel(false)}
              disabled={busy}
            >
              {t('billing.keepPlan')}
            </button>
          </div>
        ) : null}
      </>
    );
  };

  // Платная строка, которую «Текущий план» не показывает (грант с более поздним сроком).
  const maskedPaid = billing.paidMasked ? billing.paid : null;
  const maskedKind: PaidKind | null = maskedPaid ? paidRowKind(maskedPaid) : null;

  return (
    <section className={cx(pageStyles.page, styles.page)} aria-label={t('billing.title')}>
      <div className={styles.column}>
        <p className={pageStyles.eyebrow}>{t('billing.eyebrow')}</p>
        <h1 className={cx(pageStyles.title, styles.title)}>{t('billing.title')}</h1>

        {checkout.status === 'processing' ? (
          <p className={styles.banner} role="status">
            <span className={paywall.spinner} aria-hidden="true" />
            {t('billing.activating')}
          </p>
        ) : null}
        {shownError ? (
          <p className={cx(styles.banner, styles.bannerError)} role="alert">
            {shownError}
          </p>
        ) : null}

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div>
              <div className={styles.label}>{t('billing.currentPlan')}</div>
              <div className={styles.planName}>{planName}</div>
              <div className={styles.planNote}>{planNote}</div>
            </div>
            <span className={badgeClass(billing.kind)}>{badgeOf(billing.kind)}</span>
          </div>

          {/* Покупка — только без живой платной строки: с ней второй чекаут дал бы двойное списание. */}
          {billing.kind === 'free' && !billing.paid && access.isPaywallEnabled ? (
            <>
              <div className={styles.plans}>
                <PlanCards
                  prices={prices}
                  value={plan}
                  onChange={setPlan}
                  size="md"
                  disabled={busy}
                />
              </div>
              <button
                type="button"
                className={cx(paywall.primary, styles.subscribe)}
                onClick={() => subscribe(plan)}
                disabled={busy}
              >
                {checkout.status === 'opening'
                  ? t('billing.openingCheckout')
                  : t('billing.subscribe')}
              </button>
              <ul className={styles.bullets}>
                <li>{t('billing.bullet1')}</li>
                <li>{t('billing.bullet2')}</li>
                <li>{t('billing.bullet3')}</li>
              </ul>
            </>
          ) : null}
          {/* Бывший покупатель без живой подписки: счета и карта остались в портале. */}
          {billing.kind === 'free' && !billing.paid && billing.hasPaddleCustomer ? (
            <p className={styles.adminNote}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => void openPortal('invoices')}
                disabled={busy}
              >
                {t('billing.invoicesAndPayment')}
              </button>
            </p>
          ) : null}

          {billing.kind === 'active' || billing.kind === 'past_due' ? (
            <>
              <dl className={styles.facts}>
                {/* past_due без строки доступа: дата — конец последнего оплаченного периода, а не «продлится». */}
                <dt>
                  {billing.kind === 'past_due' && !user?.has_subscription
                    ? t('billing.paidPeriodEnded')
                    : t('billing.renews')}
                </dt>
                <dd>{expires}</dd>
                {paymentMethod ? (
                  <>
                    <dt>{t('billing.payment')}</dt>
                    <dd>
                      {paymentMethod.brand} ···· {paymentMethod.last4}
                    </dd>
                  </>
                ) : null}
                {billing.paddleManaged ? invoicesFact : null}
              </dl>
              {billing.kind === 'past_due' ? (
                <p className={styles.adminNote}>{t('billing.pastDueHint')}</p>
              ) : null}
              {billing.paddleManaged ? (
                renderPaidActions(billing.kind)
              ) : (
                <p className={styles.adminNote}>
                  {t(maskedPaid ? 'billing.adminPaidBelow' : 'billing.adminNote')}
                </p>
              )}
            </>
          ) : null}

          {billing.kind === 'paused' ? (
            <>
              <p className={styles.graceText}>{t('billing.pausedHint')}</p>
              {renderPaidActions('paused')}
            </>
          ) : null}

          {billing.kind === 'grace' ? (
            <>
              <p className={styles.graceText}>
                {t('billing.graceBefore')}
                <strong>{expires}</strong>
                {t('billing.graceAfter')}
              </p>
              {billing.paddleManaged ? (
                renderPaidActions('grace')
              ) : maskedPaid ? (
                <p className={styles.adminNote}>{t('billing.adminPaidBelow')}</p>
              ) : access.isPaywallEnabled ? (
                <>
                  <button
                    type="button"
                    className={cx(paywall.primary, styles.subscribe)}
                    onClick={() => subscribe(billing.plan ?? 'yearly')}
                    disabled={busy}
                  >
                    {checkout.status === 'opening'
                      ? t('billing.openingCheckout')
                      : t('billing.resubscribe')}
                  </button>
                  <p className={styles.adminNote}>{t('billing.adminResubscribe')}</p>
                </>
              ) : null}
            </>
          ) : null}
        </div>

        {/* Платная строка Paddle, замаскированная грантом: управление ею живёт здесь. */}
        {maskedPaid && maskedKind ? (
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div>
                <div className={styles.label}>{t('billing.paidSubscription')}</div>
                <div className={styles.planName}>{planNameOf(paidPlan)}</div>
                <div className={styles.planNote}>
                  {maskedKind === 'past_due'
                    ? t('billing.pastDueNote')
                    : maskedKind === 'paused'
                      ? t('billing.pausedNote')
                      : maskedKind === 'grace'
                        ? t('billing.graceNote')
                        : user?.has_subscription
                          ? t('billing.paidSeparately')
                          : billedNote(paidPlan)}
                </div>
              </div>
              <span className={badgeClass(maskedKind)}>{badgeOf(maskedKind)}</span>
            </div>
            {maskedKind !== 'paused' ? (
              <dl className={styles.facts}>
                <dt>
                  {maskedKind === 'grace'
                    ? t('billing.paidUntil')
                    : maskedKind === 'past_due'
                      ? t('billing.paidPeriodEnded')
                      : t('billing.renews')}
                </dt>
                <dd>{formatBillingDate(maskedPaid.expires_at ?? null)}</dd>
                {invoicesFact}
              </dl>
            ) : (
              <p className={styles.graceText}>{t('billing.pausedHint')}</p>
            )}
            {maskedKind === 'past_due' ? (
              <p className={styles.adminNote}>{t('billing.pastDueHint')}</p>
            ) : null}
            {renderPaidActions(maskedKind)}
          </div>
        ) : null}

        <p className={styles.footnote}>
          {t('billing.footnote')}
          <a href={`mailto:${appConfig.supportEmail}`}>{appConfig.supportEmail}</a>
        </p>
      </div>
    </section>
  );
}
